import { Button as MuiButton, CircularProgress, Tooltip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useCoverageOverview } from "@platform/coverage-overview";
import { Button } from "@platform/design-system";
import React, { useState, useEffect } from "react";
import { logger } from "@platform/browser-logger";
import { usePlanDesignerRfpIds } from "@platform/plan-grid-core";

import { useApiClient } from "../../api";
import {
  buildCoverageCompletionTooltipMessage,
  computeCompletionGate,
} from "../../util/coverageCompletionTooltip";

import type { CoverageTypeEnum, BenefitPlanTypeEnum } from "@platform/api";
import type { MissingField } from "@platform/plan-grid-core";

// Style constants
const BUTTON_TRANSITION = "all 0.3s ease-in-out";
const BUTTON_MIN_WIDTH = 160;

// Button label constants
const BUTTON_LABELS = {
  CHECKING: "Checking...",
  UPDATING: "Updating...",
  MARK_COMPLETE: "Mark Complete",
  MARK_INCOMPLETE: "Mark Incomplete",
} as const;

export interface CoverageCompletionButtonProps {
  /** Coverage name (e.g., "Dental PPO") */
  coverageName: string;
  /** Coverage type V5 enum value (e.g., "dental") */
  coverageType: CoverageTypeEnum;
  /** Optional product type (e.g., "ppo") */
  productType?: BenefitPlanTypeEnum;
  /** External disable (e.g., validation errors prevent marking complete) */
  disabled?: boolean;
  /** Whether the coverage has at least one eligibility class assigned */
  hasEligibilityClasses?: boolean;
  /** Whether all required fields have valid values */
  hasAllRequiredFields?: boolean;
  /** List of fields that are missing values */
  missingFields?: MissingField[];
  /** True when two plan groups share a Current Policy; blocks marking complete (ADR 10.22). */
  hasDuplicateCurrentPolicy?: boolean;
  /** Callback fired after successfully marking coverage complete */
  onSuccess?: () => void;
  /** Callback fired on error */
  onError?: (error: Error) => void;
}

/**
 * Button for toggling coverage (LOC) completion status
 *
 * States:
 * - Incomplete: Outlined button "Mark Complete"
 * - Loading: Spinner with "Checking..."
 * - Complete: Contained success button with checkmark "Mark Incomplete" (clickable to toggle)
 * - Marking: Spinner with "Updating..."
 *
 * @example
 * ```tsx
 * <CoverageCompletionButton
 *   coverageName="Dental PPO"
 *   coverageType="dental"
 *   productType="ppo"
 *   hasEligibilityClasses={true}
 *   onSuccess={() => console.log("Status toggled!")}
 * />
 * ```
 */
export const CoverageCompletionButton: React.FC<
  CoverageCompletionButtonProps
> = ({
  coverageName,
  coverageType,
  productType,
  disabled = false,
  hasEligibilityClasses = true,
  hasAllRequiredFields = true,
  missingFields = [],
  hasDuplicateCurrentPolicy = false,
  onSuccess,
  onError,
}) => {
  const axiosInstance = useApiClient();
  const { ecoRfpId } = usePlanDesignerRfpIds();
  const [isMarking, setIsMarking] = useState(false);

  // Log if axios instance is missing
  useEffect(() => {
    if (!axiosInstance) {
      logger.error(
        "[CoverageCompletionButton] No axios instance available from PlanDesigner configuration",
        undefined,
        {
          ecoRfpId,
          coverageName,
          coverageType,
          productType,
        },
      );
    }
  }, [axiosInstance, ecoRfpId, coverageName, coverageType, productType]);

  // Log if ecoRfpId is missing
  useEffect(() => {
    if (!ecoRfpId) {
      logger.error(
        "[CoverageCompletionButton] No ecoRfpId available from PlanDesigner configuration",
        undefined,
        {
          coverageName,
          coverageType,
          productType,
        },
      );
    }
  }, [ecoRfpId, coverageName, coverageType, productType]);

  const {
    getCompletionStatusLegacy,
    setCompletionStatusLegacy,
    isSettingCompletionLegacy,
    coverageQuery,
  } = useCoverageOverview({
    ecoRfpId: ecoRfpId!, // Non-null assertion safe because button is disabled when undefined
    axiosInstance,
  });

  const isComplete = getCompletionStatusLegacy(coverageName);
  const isLoading =
    coverageQuery.isLoading || isSettingCompletionLegacy || isMarking;

  // Determine if marking complete should be disabled. Can always mark
  // incomplete, but marking complete requires eligibility classes, required
  // fields, and no duplicate Current Policy (ADR 10.22).
  const {
    isMissingEligibilityClasses,
    isMissingRequiredFields,
    isDuplicateCurrentPolicy,
    isBlocked: isCompletionBlocked,
  } = computeCompletionGate({
    isComplete: Boolean(isComplete),
    hasEligibilityClasses,
    hasAllRequiredFields,
    hasDuplicateCurrentPolicy,
  });
  const isDisabled = disabled || isLoading || !ecoRfpId || isCompletionBlocked;

  // Log button state for debugging
  useEffect(() => {
    logger.debug("[CoverageCompletionButton] Button state updated", {
      coverageName,
      isComplete,
      isLoading,
      isDisabled,
      disabled,
      isMarking,
      isSettingCompletion: isSettingCompletionLegacy,
      hasEligibilityClasses,
      isMissingEligibilityClasses,
      hasAllRequiredFields,
      isMissingRequiredFields,
      missingFieldCount: missingFields.length,
      coverageQueryIsLoading: coverageQuery.isLoading,
      coverageQueryStatus: coverageQuery.status,
      hasAxiosInstance: !!axiosInstance,
      ecoRfpId: ecoRfpId || "NOT AVAILABLE",
    });
  }, [
    isComplete,
    isLoading,
    isDisabled,
    disabled,
    isMarking,
    isSettingCompletionLegacy,
    hasEligibilityClasses,
    isMissingEligibilityClasses,
    hasAllRequiredFields,
    isMissingRequiredFields,
    missingFields.length,
    coverageQuery.isLoading,
    coverageQuery.status,
    coverageName,
    axiosInstance,
    ecoRfpId,
  ]);

  // Reset local marking state when mutation completes
  useEffect(() => {
    if (!isSettingCompletionLegacy && isMarking) {
      setIsMarking(false);
    }
  }, [isSettingCompletionLegacy, isMarking]);

  const handleToggleCompletion = async (): Promise<void> => {
    const newCompletionStatus = !isComplete;

    logger.info("[CoverageCompletionButton] Toggle completion button clicked", {
      ecoRfpId,
      coverageName,
      coverageType,
      productType,
      currentStatus: isComplete,
      newStatus: newCompletionStatus,
      hasAxiosInstance: !!axiosInstance,
    });

    setIsMarking(true);

    try {
      logger.debug(
        "[CoverageCompletionButton] Calling setCompletionStatusLegacy",
        {
          coverageType,
          productType,
          isComplete: newCompletionStatus,
        },
      );

      await setCompletionStatusLegacy({
        coverageType,
        productType,
        isComplete: newCompletionStatus,
      });

      logger.info(
        "[CoverageCompletionButton] Successfully toggled coverage completion status",
        {
          coverageName,
          coverageType,
          newStatus: newCompletionStatus,
        },
      );

      onSuccess?.();
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));

      logger.error(
        "[CoverageCompletionButton] Failed to toggle coverage completion status",
        errorObj,
        {
          ecoRfpId,
          coverageName,
          coverageType,
          productType,
        },
      );

      onError?.(errorObj);
    } finally {
      setIsMarking(false);
    }
  };

  // Loading state - checking coverage status
  if (coverageQuery.isLoading) {
    return (
      <Button
        variant="secondary"
        disabled
        startIcon={<CircularProgress size={16} />}
        sx={{
          minWidth: BUTTON_MIN_WIDTH,
          transition: BUTTON_TRANSITION,
        }}
      >
        {BUTTON_LABELS.CHECKING}
      </Button>
    );
  }

  // Complete state - clickable success button with checkmark
  if (isComplete) {
    return (
      // TODO(ds-button): no DS variant for this status colour
      <MuiButton
        variant="contained"
        color="success"
        onClick={handleToggleCompletion}
        disabled={isDisabled}
        startIcon={
          <CheckCircleIcon
            sx={{
              animation: "stamp 0.4s ease-out",
              "@keyframes stamp": {
                "0%": {
                  transform: "scale(0) rotate(-180deg)",
                  opacity: 0,
                },
                "50%": {
                  transform: "scale(1.2) rotate(10deg)",
                },
                "100%": {
                  transform: "scale(1) rotate(0deg)",
                  opacity: 1,
                },
              },
            }}
          />
        }
        sx={{
          minWidth: BUTTON_MIN_WIDTH,
          transition: BUTTON_TRANSITION,
          "&:hover": {
            transform: "scale(1.02)",
            backgroundColor: "success.dark",
          },
          "&:active": {
            transform: "scale(0.98)",
          },
          "& .MuiButton-startIcon": {
            filter: "drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.2))",
          },
        }}
      >
        {BUTTON_LABELS.MARK_INCOMPLETE}
      </MuiButton>
    );
  }

  // Marking/updating state
  if (isMarking || isSettingCompletionLegacy) {
    return (
      <Button
        variant="primary"
        disabled
        startIcon={<CircularProgress size={16} color="inherit" />}
        sx={{
          minWidth: BUTTON_MIN_WIDTH,
          transition: BUTTON_TRANSITION,
        }}
      >
        {BUTTON_LABELS.UPDATING}
      </Button>
    );
  }

  // Build tooltip message for disabled state
  const getTooltipMessage = (): string =>
    buildCoverageCompletionTooltipMessage({
      isMissingEligibilityClasses,
      isMissingRequiredFields,
      isDuplicateCurrentPolicy,
      missingFields,
    });

  const hasTooltip = isCompletionBlocked;

  // Incomplete state - outlined button with tooltip when validation prevents completion
  return (
    <Tooltip
      title={getTooltipMessage()}
      arrow
      disableHoverListener={!hasTooltip}
      componentsProps={{
        tooltip: { sx: { whiteSpace: "pre-line", maxWidth: 320 } },
      }}
    >
      <span>
        <Button
          variant="secondary"
          onClick={handleToggleCompletion}
          disabled={isDisabled}
          startIcon={<RadioButtonUncheckedIcon />}
          sx={{
            minWidth: BUTTON_MIN_WIDTH,
            transition: BUTTON_TRANSITION,
            "&:hover": {
              transform: "scale(1.02)",
            },
            "&:active": {
              transform: "scale(0.98)",
            },
          }}
        >
          {BUTTON_LABELS.MARK_COMPLETE}
        </Button>
      </span>
    </Tooltip>
  );
};
